/**
 * Ô chọn địa bàn dạng cây có tìm kiếm (kiểu TreeSelect của bản mẫu): đóng thì hiện
 * tên đang chọn, mở thì có ô tìm ở trên và cây tỉnh → xã/phường bên dưới.
 *
 *   - Giá trị rỗng '' = toàn bộ phạm vi; khi cây có nút tỉnh, nút tỉnh đại diện
 *     cho giá trị rỗng (bấm vào tỉnh = xem cả tỉnh), đúng như bản mẫu.
 *   - Tìm không phân biệt dấu và hoa/thường ("xa son lo" khớp "xã Sông Lô").
 *   - Bàn phím: ↑/↓ di chuyển, Enter chọn, Esc đóng; bấm ra ngoài thì đóng.
 *   - Tài khoản cấp xã: ô bị KHOÁ vào xã được phân công (đọc từ ngữ cảnh phiên), chỉ
 *     hiện tên xã, không mở được và không có "Toàn tỉnh". Đây là chỗ dùng chung của
 *     mọi bộ lọc địa bàn (cụm loa, bảng tin, lịch phát, nhật ký, tổng quan…).
 */
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { dungPhien } from '../trang-thai/ngu-canh-phien';
import { viQuanTri } from '../ngon-ngu/vi-quan-tri';

export interface NutDiaBanCay {
  maDinhDanh: string;
  ten: string;
  cap: 'tinh' | 'xa' | 'thon';
  maCha: string | null;
}

/** Bỏ dấu tiếng Việt, chữ thường — để so khớp khi tìm. */
export const boDau = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase().trim();

interface MucHienThi {
  gt: string;
  ten: string;
  cap: 'tinh' | 'xa' | 'thon' | 'tat-ca';
  maTinh?: string;
}

export function ChonDiaBanCay({
  id,
  nhan,
  giaTri,
  cay,
  onDoi,
  nhanTatCa,
  goiYTim = 'Tìm địa bàn…',
  className,
  boQuaKhoaXa = false,
}: {
  id: string;
  /** Tên trường cho trình đọc màn hình (aria-label). */
  nhan: string;
  giaTri: string;
  cay: NutDiaBanCay[];
  onDoi: (v: string) => void;
  /** Nhãn cho giá trị rỗng khi cây không có nút tỉnh. */
  nhanTatCa: string;
  goiYTim?: string;
  className?: string;
  /**
   * Không khoá theo xã của phiên — chỉ dùng ở biểu mẫu quản trị chọn xã cho NGƯỜI KHÁC
   * (MH-34), nơi người thao tác luôn là cấp tỉnh.
   */
  boQuaKhoaXa?: boolean;
}): ReactNode {
  const { khoaDiaBan } = dungPhien();
  const [mo, datMo] = useState(false);
  const [tim, datTim] = useState('');
  const [dangTro, datDangTro] = useState(0);
  const [dong, datDong] = useState<Set<string>>(new Set());
  const goc = useRef<HTMLDivElement | null>(null);
  const oTim = useRef<HTMLInputElement | null>(null);
  const idDs = useId();

  const dsTinh = useMemo(() => cay.filter((n) => n.cap === 'tinh'), [cay]);
  const dsXa = useMemo(() => cay.filter((n) => n.cap === 'xa'), [cay]);

  const tenDangChon = useMemo(() => {
    if (!giaTri) return dsTinh.length === 1 ? dsTinh[0]!.ten : nhanTatCa;
    return cay.find((n) => n.maDinhDanh === giaTri)?.ten ?? giaTri;
  }, [giaTri, cay, dsTinh, nhanTatCa]);

  /** Danh sách mục đang hiện (đã lọc theo ô tìm, bỏ con của tỉnh đang thu gọn). */
  const mucHien = useMemo((): MucHienThi[] => {
    const q = boDau(tim);
    const khop = (ten: string): boolean => !q || boDau(ten).includes(q);
    const kq: MucHienThi[] = [];
    if (dsTinh.length === 0) {
      kq.push({ gt: '', ten: nhanTatCa, cap: 'tat-ca' });
      for (const x of dsXa) if (khop(x.ten)) kq.push({ gt: x.maDinhDanh, ten: x.ten, cap: 'xa' });
      return kq;
    }
    for (const t of dsTinh) {
      const con = dsXa.filter(
        (x) => (x.maCha === t.maDinhDanh || x.maDinhDanh.startsWith(`${t.maDinhDanh}.`)) && khop(x.ten),
      );
      if (q && con.length === 0 && !khop(t.ten)) continue;
      // Một tỉnh duy nhất = toàn bộ phạm vi → giá trị rỗng.
      kq.push({ gt: dsTinh.length === 1 ? '' : t.maDinhDanh, ten: t.ten, cap: 'tinh', maTinh: t.maDinhDanh });
      if (q || !dong.has(t.maDinhDanh))
        for (const x of con) kq.push({ gt: x.maDinhDanh, ten: x.ten, cap: 'xa' });
    }
    return kq;
  }, [tim, dsTinh, dsXa, dong, nhanTatCa]);

  useEffect(() => {
    if (!mo) return undefined;
    oTim.current?.focus();
    const ngoai = (e: MouseEvent): void => {
      if (goc.current && !goc.current.contains(e.target as Node)) datMo(false);
    };
    document.addEventListener('mousedown', ngoai);
    return () => document.removeEventListener('mousedown', ngoai);
  }, [mo]);

  useEffect(() => {
    // Mở ra thì trỏ vào mục đang chọn.
    if (mo)
      datDangTro(
        Math.max(
          0,
          mucHien.findIndex((m) => m.gt === giaTri),
        ),
      );
  }, [mo]); // chỉ khi vừa mở — không chạy lại mỗi lần gõ tìm

  const chon = (gt: string): void => {
    onDoi(gt);
    datMo(false);
    datTim('');
  };

  const phim = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      datMo(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      datDangTro((i) => Math.min(mucHien.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      datDangTro((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const m = mucHien[dangTro];
      if (m) chon(m.gt);
    }
  };

  if (khoaDiaBan && !boQuaKhoaXa) {
    // Giá trị là một nút con của xã (thôn) thì hiện tên nút đó; còn lại hiện tên xã.
    const laNutCon = giaTri.startsWith(`${khoaDiaBan.ma}.`);
    const ten = laNutCon ? (cay.find((n) => n.maDinhDanh === giaTri)?.ten ?? giaTri) : khoaDiaBan.ten;
    return (
      <div className={`dbc dbc--khoa${className ? ` ${className}` : ''}`} data-khoa-dia-ban={khoaDiaBan.ma}>
        <button
          type="button"
          id={id}
          className="dbc__nut"
          aria-label={nhan}
          aria-disabled="true"
          disabled
          title={viQuanTri.taiKhoanXa.diaBanCoDinh}
        >
          <span className="dbc__chu">{ten}</span>
        </button>
      </div>
    );
  }

  const doiThuGon = (ma: string): void =>
    datDong((s) => {
      const moi = new Set(s);
      if (moi.has(ma)) moi.delete(ma);
      else moi.add(ma);
      return moi;
    });

  return (
    <div className={`dbc${className ? ` ${className}` : ''}`} ref={goc}>
      <button
        type="button"
        id={id}
        className="dbc__nut"
        aria-label={nhan}
        aria-haspopup="listbox"
        aria-expanded={mo}
        onClick={() => datMo((x) => !x)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !mo) {
            e.preventDefault();
            datMo(true);
          }
        }}
      >
        <span className="dbc__chu">{tenDangChon}</span>
        <svg width={14} height={14} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
        </svg>
      </button>
      {mo ? (
        <div className="dbc__hop">
          <div className="dbc__tim">
            <input
              ref={oTim}
              type="search"
              role="combobox"
              aria-label={`${nhan} — tìm`}
              aria-controls={idDs}
              aria-expanded="true"
              aria-activedescendant={mucHien[dangTro] ? `${idDs}-${dangTro}` : undefined}
              placeholder={goiYTim}
              value={tim}
              onChange={(e) => {
                datTim(e.target.value);
                datDangTro(0);
              }}
              onKeyDown={phim}
            />
            <svg width={14} height={14} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <circle cx="11" cy="11" r="6" fill="none" stroke="currentColor" strokeWidth={2} />
              <path d="M20 20l-4.5-4.5" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
            </svg>
          </div>
          <ul className="dbc__ds" role="listbox" id={idDs} aria-label={nhan}>
            {mucHien.length === 0 ? <li className="dbc__rong">Không tìm thấy địa bàn</li> : null}
            {mucHien.map((m, i) => (
              <li
                key={`${m.cap}-${m.maTinh ?? m.gt}`}
                id={`${idDs}-${i}`}
                role="option"
                aria-selected={m.gt === giaTri}
                className={`dbc__muc dbc__muc--${m.cap}${m.gt === giaTri ? ' dbc__muc--chon' : ''}${i === dangTro ? ' dbc__muc--tro' : ''}`}
                onMouseEnter={() => datDangTro(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => chon(m.gt)}
              >
                {m.cap === 'tinh' && m.maTinh ? (
                  <span
                    className={`dbc__mui${dong.has(m.maTinh) && !tim ? ' dbc__mui--dong' : ''}`}
                    aria-hidden="true"
                    onClick={(e) => {
                      e.stopPropagation();
                      doiThuGon(m.maTinh as string);
                    }}
                  >
                    ▾
                  </span>
                ) : null}
                <span className="dbc__ten">{m.ten}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
